import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import { Plus, X } from "lucide-react";
import AppButton from "../../../common_components/AppButton";
import { Dropdown, TextInput } from "../../../common_components/FormInputs";
let filterSeq = 0;
const nextFilterId = () => {
    filterSeq += 1;
    return `flt_${Date.now().toString(36)}_${filterSeq}`;
};
const OPERATOR_LABELS = {
    contains: "contains",
    does_not_contain: "does not contain",
};
const operatorLabel = (key) => OPERATOR_LABELS[key] || key;
/** A filter is only worth keeping once it has a value. */
const hasValue = (filter) => filter.values.some((value) => value.trim());
const NetworkFilters = ({ fields, operators, filters, onChange, disabled = false }) => {
    const fieldLabel = (key) => fields.find((f) => f.key === key)?.label || key;
    const conditions = operators.length ? operators : ["contains", "does_not_contain"];
    /** The filter being edited — a copy; `isNew` when it came from + Add filter. */
    const [draft, setDraft] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const addRef = useRef(null);
    if (fields.length === 0)
        return null;
    const openNew = () => {
        const used = new Set(filters.map((f) => f.field_id));
        const field = fields.find((f) => !used.has(f.key)) || fields[0];
        setDraft({
            id: nextFilterId(),
            field_id: field.key,
            operator: conditions[0],
            values: [],
            isNew: true,
        });
        setAnchorEl(addRef.current);
    };
    const openEdit = (filter, el) => {
        if (disabled)
            return;
        setDraft({ ...filter, values: [...filter.values], isNew: false });
        setAnchorEl(el);
    };
    const close = () => {
        setDraft(null);
        setAnchorEl(null);
    };
    const apply = () => {
        if (!draft)
            return;
        const { isNew, ...filter } = draft;
        onChange(isNew ? [...filters, filter] : filters.map((f) => (f.id === filter.id ? filter : f)));
        close();
    };
    const remove = (id) => onChange(filters.filter((f) => f.id !== id));
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.75, px: 1.25, py: 1 }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#475467", fontWeight: 600 }, children: "Filters" }), _jsxs(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.75, minWidth: 0 }, children: [filters.filter(hasValue).map((filter) => {
                        const value = filter.values.filter((v) => v.trim()).join(", ");
                        return (_jsxs(Box, { role: "button", tabIndex: disabled ? -1 : 0, onClick: (e) => openEdit(filter, e.currentTarget), onKeyDown: (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openEdit(filter, e.currentTarget);
                                }
                            }, title: "Edit filter", sx: {
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                maxWidth: "100%",
                                minHeight: 28,
                                py: 0.375,
                                pl: 1,
                                pr: disabled ? 1 : 0.5,
                                borderRadius: "6px",
                                border: "1px solid #D0D5DD",
                                bgcolor: "#F9FAFB",
                                cursor: disabled ? "default" : "pointer",
                                transition: "border-color .15s ease, background-color .15s ease",
                                "&:hover": disabled ? undefined : { borderColor: "#98A2B3", bgcolor: "#fff" },
                                "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
                            }, children: [_jsxs(Typography, { component: "span", sx: { color: "#344054", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4, fontSize: "12px" }, children: [fieldLabel(filter.field_id), " ", operatorLabel(filter.operator), " ", _jsxs(Box, { component: "span", sx: { fontWeight: 600, color: "#101828" }, children: ["\u201C", value, "\u201D"] })] }), !disabled && (_jsx(Box, { role: "button", "aria-label": "Remove filter", tabIndex: 0, onClick: (e) => {
                                        e.stopPropagation();
                                        remove(filter.id);
                                    }, onKeyDown: (e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            remove(filter.id);
                                        }
                                    }, sx: {
                                        display: "flex",
                                        p: 0.25,
                                        borderRadius: "4px",
                                        color: "#667085",
                                        "&:hover": { bgcolor: "#FEF3F2", color: "#D92D20" },
                                    }, children: _jsx(X, { size: 12, strokeWidth: 2.5 }) }))] }, filter.id));
                    }), !disabled && (_jsxs(Box, { component: "button", type: "button", ref: addRef, onClick: openNew, sx: {
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            height: 28,
                            px: 1,
                            borderRadius: "6px",
                            border: "1px dashed #D0D5DD",
                            bgcolor: "#fff",
                            color: "#475467",
                            fontFamily: "inherit",
                            fontSize: "12px",
                            fontWeight: 800,
                            cursor: "pointer",
                            transition: "border-color .15s ease, color .15s ease",
                            "&:hover": { borderColor: "#FF008E", color: "#FF008E" },
                        }, children: [_jsx(Plus, { size: 13 }), "Add filter"] })), disabled && filters.filter(hasValue).length === 0 && (_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#98A2B3" }, children: "None" }))] }), _jsx(Popover, { open: Boolean(draft && anchorEl), anchorEl: anchorEl, onClose: close, anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" }, slotProps: {
                    paper: {
                        sx: {
                            mt: 0.75,
                            width: 300,
                            p: 1.5,
                            borderRadius: "12px",
                            border: "1px solid #EAECF0",
                            boxShadow: "0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)",
                        },
                    },
                }, children: draft && (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.25 }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 600, color: "#101828" }, children: draft.isNew ? "Add filter" : "Edit filter" }), fields.length > 1 && (_jsx(Dropdown, { label: "Field", size: "sm", usePortal: true, sortByLabel: false, value: draft.field_id, options: fields.map((f) => ({ value: f.key, label: f.label })), onChange: (value) => setDraft({ ...draft, field_id: value }) })), _jsx(Dropdown, { label: fields.length > 1 ? "Condition" : `${fieldLabel(draft.field_id)}`, size: "sm", usePortal: true, sortByLabel: false, value: draft.operator, options: conditions.map((key) => ({ value: key, label: operatorLabel(key) })), onChange: (value) => setDraft({ ...draft, operator: value }) }), _jsx(TextInput, { label: "Value", size: "sm", autoFocus: true, value: draft.values[0] || "", placeholder: "e.g. Spring", onChange: (e) => setDraft({ ...draft, values: e.target.value ? [e.target.value] : [] }), onKeyDown: (e) => {
                                if (e.key === "Enter" && hasValue(draft))
                                    apply();
                            } }), _jsxs(Box, { sx: { display: "flex", justifyContent: "flex-end", gap: 1, pt: 0.5 }, children: [_jsx(AppButton, { variant: "outlined", onClick: close, sx: { height: 34, textTransform: "none", borderColor: "#D0D5DD", color: "#344054" }, children: "Cancel" }), _jsx(AppButton, { variant: "contained", disabled: !hasValue(draft), onClick: apply, sx: {
                                        height: 34,
                                        textTransform: "none",
                                        bgcolor: "#FF008E",
                                        boxShadow: "none",
                                        "&:hover": { bgcolor: "#E6007A" },
                                    }, children: "Apply" })] })] })) })] }));
};
export default NetworkFilters;
